import React, { useEffect } from 'react';
import framework from 'utils/framework';
const { wrapFunctionalComponent } = framework;
import cookieUtils from 'utils/Cookies';
import store from 'Store';
import Actions from 'Actions';
import Location from 'utils/Location';
import Storage from 'utils/localStorage/Storage';
import LOCAL_STORAGE from 'utils/localStorage/Constants';
import userUtils from 'utils/User';
import authenticationUtils from 'utils/Authentication';
import RCPSCookies from 'utils/RCPSCookies';
import { loginSSO } from 'services/api/authentication/generateLithiumSsoToken';
import {
    REDIRECT_MAP,
    DEFAULT_REDIRECT,
    MAX_COOKIE_SIZE,
    AGENT_HANDOFF_REFERRAL_KEY,
    AGENT_SOURCE,
    AGENT_SOURCE_PARAM,
    AGENT_SOURCE_EVENT_MAP
} from 'constants/AgentHandoff';

/**
 * AgentHandoff Page
 * Silent interstitial page that clears all storage, merges cart items from query params
 * into cookies, and redirects to the specified URL. No header/footer, just shows loading state.
 *
 * Query Parameters:
 * - cart: Cart items in format SKU_ID:QUANTITY,SKU_ID:QUANTITY (e.g., "2925006:1,2940500:2")
 *         Using colon (:) as SKU-quantity separator and comma (,) as item separator
 *         Only digits, colons, and commas are allowed.
 * - redirect: Key from REDIRECT_MAP (e.g., "basket", "home", "checkout")
 * - clear_storage: "true" to clear localStorage and sessionStorage
 */

const isValidCartFormat = cart => /^[\d:,]+$/.test(cart);

const getRedirectUrl = redirectKey => {
    if (!redirectKey) {
        return DEFAULT_REDIRECT;
    }

    return REDIRECT_MAP[redirectKey] || DEFAULT_REDIRECT;
};

const parseCartParam = cart => {
    return cart.split(',').map(item => {
        const [skuId, quantity] = item.split(':');
        const qty = parseInt(quantity, 10);

        if (isNaN(qty) || qty < 1) {
            throw new Error(`Invalid quantity: ${quantity}`);
        }

        return { skuId, quantity: qty };
    });
};

const parseAtgCookie = content => {
    const items = new Map();

    if (content) {
        const parts = content.split('-');

        for (let i = 0; i < parts.length; i += 2) {
            if (parts[i] && parts[i + 1]) {
                items.set(parts[i], parseInt(parts[i + 1], 10));
            }
        }
    }

    return items;
};

const parseCncCookie = content => {
    const items = new Map();

    if (content) {
        const withoutPrefix = content.replace(/^STH-/, '');

        if (withoutPrefix) {
            withoutPrefix.split('_').forEach(part => {
                const [skuId, quantity] = part.split('-');

                if (skuId && quantity) {
                    items.set(skuId, parseInt(quantity, 10));
                }
            });
        }
    }

    return items;
};

const buildAtgCookieValue = items => {
    return Array.from(items.entries())
        .map(([skuId, quantity]) => `${skuId}-${quantity}`)
        .join('-');
};

const buildCncCookieValue = items => {
    return (
        'STH-' +
        Array.from(items.entries())
            .map(([skuId, quantity]) => `${skuId}-${quantity}`)
            .join('_')
    );
};

const mergeCartIntoCookies = cart => {
    const existingAtgContent = cookieUtils.read('ATG_ORDER_CONTENT') || '';
    const existingCncContent = cookieUtils.read('CNC_ORDER_CONTENT') || '';

    try {
        const newItems = parseCartParam(cart);
        const atgItems = parseAtgCookie(existingAtgContent);
        const cncItems = parseCncCookie(existingCncContent);

        // Merge new items into existing (add quantities if SKU exists)
        newItems.forEach(({ skuId, quantity }) => {
            atgItems.set(skuId, (atgItems.get(skuId) || 0) + quantity);
            cncItems.set(skuId, (cncItems.get(skuId) || 0) + quantity);
        });

        const atgContent = buildAtgCookieValue(atgItems);
        const cncContent = buildCncCookieValue(cncItems);

        if (atgContent.length > MAX_COOKIE_SIZE || cncContent.length > MAX_COOKIE_SIZE) {
            Sephora.logger.error('Cart too large for cookie storage');
        } else {
            cookieUtils.write('ATG_ORDER_CONTENT', atgContent, 30, false, false);
            cookieUtils.write('CNC_ORDER_CONTENT', cncContent, 30, false, false);
        }
    } catch (error) {
        Sephora.logger.error('Error merging cart:', error);

        // On error, restore existing cookies
        if (existingAtgContent) {
            cookieUtils.write('ATG_ORDER_CONTENT', existingAtgContent, 30, false, false);
        }

        if (existingCncContent) {
            cookieUtils.write('CNC_ORDER_CONTENT', existingCncContent, 30, false, false);
        }
    }
};

const clearBrowserStorage = () => {
    try {
        localStorage.clear();
        sessionStorage.clear();
    } catch (error) {
        Sephora.logger.error('Error clearing storage:', error);
    }
};

// Persists the agent handoff session flag only when the handoff URL carries a
// recognized agent_source. The recognized source string is stored so analytics
// can route to the correct global session event (e.g. event313 for chatgpt,
// event321 for google). Absent or unrecognized sources fire no agentic event.
const setAgentHandoffFlag = agentSource => {
    if (agentSource && AGENT_SOURCE_EVENT_MAP[agentSource]) {
        Storage.session.setItem(AGENT_HANDOFF_REFERRAL_KEY, agentSource);
    }
};

const handleSSOTokenAuth = async ssoToken => {
    const response = await loginSSO(ssoToken);

    const { isAuthServiceEnabled } = authenticationUtils;

    if (isAuthServiceEnabled()) {
        authenticationUtils.updateProfileStatus({
            profileSecurityStatus: [response?.tokens?.profileSecurityStatus],
            accessToken: [response?.tokens?.accessToken, response?.tokens?.atExp],
            refreshToken: [response?.tokens?.refreshToken, response?.tokens?.rtExp],
            hasIdentity: [true],
            isNewAuthFlow: RCPSCookies.isRCPSCookieBasedAuthEnabled()
        });

        Storage.local.setItem(LOCAL_STORAGE.USER_EMAIL, response?.userName);
    }

    return response;
};

const stripSSOTokenFromUrl = () => {
    const url = new URL(window.location.href);
    url.searchParams.delete('ssoToken');
    window.history.replaceState({}, '', url.toString());
};

const AgentHandoff = () => {
    useEffect(() => {
        const handleHandoff = async () => {
            store.dispatch(Actions.showInterstice(true));

            const urlParams = new URLSearchParams(window.location.search);
            const cartParam = urlParams.get('cart');
            const redirectUrl = getRedirectUrl(urlParams.get('redirect'));
            const clearStorage = urlParams.get('clear_storage') === 'true';
            const ssoToken = urlParams.get('ssoToken');
            // ConfigHub kill switch. When multi-agent handoff is disabled (default),
            // treat every incoming handoff as ChatGPT (event313) regardless of the
            // agent_source param, preserving the legacy flow until partner apps ship
            // the param. When enabled, route by the agent_source param.
            const agentSource = Sephora.configurationSettings?.isMultiAgentHandoffEnabled ? urlParams.get(AGENT_SOURCE_PARAM) : AGENT_SOURCE.CHATGPT;

            if (ssoToken) {
                if (Sephora.configurationSettings?.integrationLoginSSOEnabled && !userUtils.isSignedIn()) {
                    try {
                        await handleSSOTokenAuth(ssoToken);
                    } catch (error) {
                        Sephora.logger.warn('[AgentHandoff] loginSSO failed, proceeding as guest:', error);
                    }
                }

                stripSSOTokenFromUrl();
            }

            const cart = cartParam && isValidCartFormat(cartParam) ? cartParam : null;

            if (cartParam && !cart) {
                Sephora.logger.error('Invalid cart format');
            }

            if (!cart && !clearStorage) {
                setAgentHandoffFlag(agentSource);
                Location.setLocation(redirectUrl);

                return;
            }

            if (clearStorage) {
                clearBrowserStorage();
            }

            if (cart) {
                mergeCartIntoCookies(cart);
            }

            setAgentHandoffFlag(agentSource);
            Location.setLocation(redirectUrl);
        };

        handleHandoff();
    }, []);

    return null;
};

export default wrapFunctionalComponent(AgentHandoff, 'AgentHandoff');
